import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Search, Send, CheckCircle, XCircle, AlertTriangle, ExternalLink, Eye, Loader2, RefreshCw, FileText, Linkedin, X, Save, Pencil, ChevronRight, Mail, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const INDUSTRIES = ['Automotive', 'Medical', 'Real Estate', 'Tech', 'Finance', 'Legal', 'Insurance', 'Construction', 'Dental', 'Retail', 'Marketing/Media', 'Manufacturing', 'Food & Hospitality', 'Agriculture', 'Energy', 'Logistics', 'Education', 'Other'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

function ScoreBadge({ score, tier, small }) {
  const color = score >= 70 ? 'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/20' : score >= 40 ? 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' : 'text-[#FF4343] bg-[#FF4343]/10 border-[#FF4343]/20';
  return <span className={`inline-flex items-center gap-1 font-mono ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1'} border ${color}`}>{score}</span>;
}

function ModelBadge({ model }) {
  if (!model) return null;
  return <span className="text-[9px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5 ml-1">{model}</span>;
}

// ==================== LIST BUILDER ====================
export function ListBuilderTab() {
  const [industries, setIndustries] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [states, setStates] = useState([]);
  const [count, setCount] = useState(25);
  const [prospectType, setProspectType] = useState('executive');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState({});

  const addKeyword = (e) => {
    if (e.key === 'Enter' && keywordInput.trim()) {
      e.preventDefault();
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/prospecting/v2/search`, {
        industries, title_keywords: keywords, states, count, type: prospectType
      }, { headers: getAuthHeaders() });
      setResults(data.results || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Search failed');
    } finally { setLoading(false); }
  };

  const handleAdd = async (prospect) => {
    setAdding({ ...adding, [prospect.linkedin_url || prospect.email]: 'adding' });
    try {
      const { data } = await axios.post(`${API}/prospecting/v2/add`, {
        ...prospect, type: prospectType
      }, { headers: getAuthHeaders() });
      if (data.status === 'suppressed') {
        setAdding({ ...adding, [prospect.linkedin_url || prospect.email]: 'suppressed' });
      } else {
        setAdding({ ...adding, [prospect.linkedin_url || prospect.email]: 'added' });
      }
    } catch (e) {
      setAdding({ ...adding, [prospect.linkedin_url || prospect.email]: 'error' });
    }
  };

  return (
    <div data-testid="list-builder-tab" className="space-y-6">
      {/* Search Form */}
      <div className="bg-[#111827] border border-white/10 p-6">
        <h3 className="text-sm font-semibold mb-4">Apollo List Builder</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Prospect Type</label>
            <div className="flex gap-2">
              {['executive', 'brand'].map(t => (
                <button key={t} onClick={() => setProspectType(t)} data-testid={`type-${t}`}
                  className={`text-xs px-4 py-2 border ${prospectType === t ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#9CA3AF]'}`}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Results</label>
            <select value={count} onChange={e => setCount(Number(e.target.value))} data-testid="result-count"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343]">
              <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Industries</label>
            <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
              {INDUSTRIES.map(i => (
                <button key={i} onClick={() => setIndustries(industries.includes(i) ? industries.filter(x => x !== i) : [...industries, i])}
                  className={`text-[10px] px-2 py-1 border ${industries.includes(i) ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>{i}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">Title Keywords</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {keywords.map((k, i) => (
                <span key={i} className="text-xs px-2 py-1 bg-[#FF4343]/10 text-[#FF4343] border border-[#FF4343]/20 flex items-center gap-1">
                  {k} <button onClick={() => setKeywords(keywords.filter((_, j) => j !== i))} className="text-[#FF4343]/60 hover:text-[#FF4343]">&times;</button>
                </span>
              ))}
            </div>
            <input value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={addKeyword}
              placeholder="Type keyword + Enter" data-testid="keyword-input"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343]" />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-1 block">US States (optional)</label>
          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
            {US_STATES.map(s => (
              <button key={s} onClick={() => setStates(states.includes(s) ? states.filter(x => x !== s) : [...states, s])}
                className={`text-[9px] px-1.5 py-0.5 border ${states.includes(s) ? 'border-[#FF4343] text-[#FF4343]' : 'border-white/10 text-[#6B7280]'}`}>{s}</button>
            ))}
          </div>
        </div>
        <button onClick={handleSearch} disabled={loading} data-testid="apollo-search-btn"
          className="flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-5 py-2 text-sm font-semibold transition-all disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</> : <><Search className="w-4 h-4" /> Search Apollo</>}
        </button>
      </div>

      {/* Results Table */}
      {results.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="search-results-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">AI</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Name</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Title</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Company</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Industry</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Location</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Email</th>
                <th className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, i) => {
                const key = r.linkedin_url || r.email || i;
                const addStatus = adding[key];
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2">
                      {r.ai_score !== undefined ? (
                        <div className="flex flex-col items-start gap-0.5">
                          <ScoreBadge score={r.ai_score} tier={r.ai_tier} small />
                          <ModelBadge model={r.ai_model_used} />
                        </div>
                      ) : <Loader2 className="w-3 h-3 text-[#6B7280] animate-spin" />}
                    </td>
                    <td className="py-2 text-white text-xs">{r.name}</td>
                    <td className="py-2 text-[#9CA3AF] text-xs max-w-[150px] truncate">{r.title}</td>
                    <td className="py-2 text-[#9CA3AF] text-xs">{r.company}</td>
                    <td className="py-2 text-[#9CA3AF] text-xs">{r.industry}</td>
                    <td className="py-2 text-[#6B7280] text-xs">{r.city}{r.state ? `, ${r.state}` : ''}</td>
                    <td className="py-2 text-[#6B7280] text-xs max-w-[120px] truncate">{r.email || '—'}</td>
                    <td className="py-2">
                      <div className="flex gap-1.5 items-center">
                        {r.already_in_system ? (
                          <span className="text-[10px] font-mono text-[#6B7280]">In system</span>
                        ) : addStatus === 'added' ? (
                          <span className="text-[10px] font-mono text-[#10B981]">Added</span>
                        ) : addStatus === 'suppressed' ? (
                          <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5">Suppressed</span>
                        ) : (
                          <button onClick={() => handleAdd(r)} disabled={addStatus === 'adding'} data-testid={`add-prospect-${i}`}
                            className="text-[10px] px-2 py-1 border border-[#FF4343]/50 text-[#FF4343] hover:bg-[#FF4343] hover:text-white transition-colors disabled:opacity-50">
                            {addStatus === 'adding' ? '...' : 'Add to Outreach'}
                          </button>
                        )}
                        {r.linkedin_url && (
                          <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#6B7280] hover:text-white">
                            <Linkedin className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                      {r.ai_score < 40 && !r.already_in_system && addStatus !== 'added' && (
                        <div className="text-[9px] text-yellow-400 mt-0.5 flex items-center gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" /> Low score — review before outreach
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10px] text-[#6B7280] mt-2">{results.length} results</p>
        </div>
      )}
    </div>
  );
}

// ==================== OUTREACH QUEUE ====================
export function OutreachQueueTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [preview, setPreview] = useState(null);

  useEffect(() => { fetchQueue(); }, []);

  const fetchQueue = async () => {
    try {
      const { data } = await axios.get(`${API}/outreach/queue`, { headers: getAuthHeaders() });
      setQueue(data);
    } catch {} finally { setLoading(false); }
  };

  const handleSend = async (prospect) => {
    setSending({ ...sending, [prospect.id]: true });
    try {
      await axios.post(`${API}/outreach/send`, { prospect_id: prospect.id, step: prospect.next_step }, { headers: getAuthHeaders() });
      fetchQueue();
    } catch (e) {
      alert(e.response?.data?.detail || 'Send failed');
    } finally { setSending({ ...sending, [prospect.id]: false }); }
  };

  const handleMarkSent = async (prospect) => {
    try {
      await axios.post(`${API}/outreach/mark-sent`, { prospect_id: prospect.id, step: prospect.next_step }, { headers: getAuthHeaders() });
      fetchQueue();
    } catch {}
  };

  const execQueue = queue.filter(p => p.type === 'executive');
  const brandQueue = queue.filter(p => p.type === 'brand');

  const renderGroup = (items, label) => (
    items.length > 0 && (
      <div className="mb-6">
        <h4 className="text-xs font-mono uppercase tracking-wider text-[#6B7280] mb-3">{label} ({items.length})</h4>
        <div className="space-y-2">
          {items.map(p => (
            <div key={p.id} className="bg-[#111827] border border-white/10 p-3 flex items-center justify-between" data-testid={`queue-item-${p.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <ScoreBadge score={p.ai_score || 50} small />
                <div className="min-w-0">
                  <span className="text-xs text-white font-medium">{p.name}</span>
                  <span className="text-[10px] text-[#6B7280] ml-2">{p.company}</span>
                </div>
                <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5">Step {p.next_step}</span>
                {p.channel === 'linkedin' && (
                  <span className="text-[10px] font-mono text-yellow-400 bg-yellow-400/10 px-1.5 py-0.5 border border-yellow-400/20">Manual Action Required</span>
                )}
              </div>
              <div className="flex gap-2 items-center flex-shrink-0">
                {p.channel === 'email' ? (
                  <>
                    <button onClick={() => setPreview(p)} className="text-[10px] px-2 py-1 border border-white/10 text-[#9CA3AF] hover:text-white transition-colors">
                      <Eye className="w-3 h-3 inline mr-1" />Preview
                    </button>
                    <button onClick={() => handleSend(p)} disabled={sending[p.id]} data-testid={`send-${p.id}`}
                      className="text-[10px] px-3 py-1 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-white transition-colors disabled:opacity-50">
                      <Send className="w-3 h-3 inline mr-1" />{sending[p.id] ? 'Sending...' : 'Send'}
                    </button>
                  </>
                ) : (
                  <>
                    {p.linkedin_url && (
                      <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer"
                        className="text-[10px] px-2 py-1 border border-white/10 text-[#9CA3AF] hover:text-white transition-colors">
                        Open Profile <ExternalLink className="w-3 h-3 inline ml-0.5" />
                      </a>
                    )}
                    <button onClick={() => handleMarkSent(p)} data-testid={`mark-sent-${p.id}`}
                      className="text-[10px] px-3 py-1 border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white transition-colors">
                      Mark Sent
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  );

  if (loading) return <p className="text-[#9CA3AF]">Loading queue...</p>;

  return (
    <div data-testid="outreach-queue-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">{queue.length} prospects ready for outreach</h3>
        <button onClick={fetchQueue} className="text-[#6B7280] hover:text-white"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {queue.length === 0 ? <p className="text-[#6B7280] text-sm">No outreach due today.</p> : (
        <>
          {renderGroup(execQueue, 'Sequence A — Executive Recruitment')}
          {renderGroup(brandQueue, 'Sequence B — Brand Outreach')}
        </>
      )}
      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPreview(null)} />
          <div className="relative glass max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" data-testid="preview-modal">
            <h3 className="text-sm font-semibold mb-3">Email Preview — Step {preview.next_step}</h3>
            <div className="mb-3">
              <span className="text-[10px] font-mono text-[#6B7280]">Subject:</span>
              <p className="text-sm text-white mt-1">{preview.template_subject}</p>
            </div>
            <div className="mb-4">
              <span className="text-[10px] font-mono text-[#6B7280]">Body:</span>
              <pre className="text-xs text-[#9CA3AF] mt-1 whitespace-pre-wrap font-sans leading-relaxed bg-[#090E33] border border-white/10 p-3">{preview.template_body}</pre>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { handleSend(preview); setPreview(null); }}
                className="text-xs px-4 py-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] font-semibold hover:bg-[#E03030] transition-colors">
                Confirm & Send
              </button>
              <button onClick={() => setPreview(null)} className="text-xs px-4 py-2 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== OUTREACH TRACKER ====================
export function OutreachTrackerTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seqFilter, setSeqFilter] = useState('');

  useEffect(() => { fetchLogs(); }, [seqFilter]);

  const fetchLogs = async () => {
    try {
      const params = seqFilter ? `?sequence=${seqFilter}` : '';
      const { data } = await axios.get(`${API}/outreach/tracker${params}`, { headers: getAuthHeaders() });
      setLogs(data);
    } catch {} finally { setLoading(false); }
  };

  return (
    <div data-testid="outreach-tracker-tab">
      <div className="flex gap-2 mb-4">
        {['', 'A', 'B'].map(s => (
          <button key={s} onClick={() => setSeqFilter(s)}
            className={`text-xs px-3 py-1.5 border ${seqFilter === s ? 'border-[#FF4343] text-[#FF4343] bg-[#FF4343]/5' : 'border-white/10 text-[#6B7280]'}`}>
            {s === '' ? 'All' : `Sequence ${s}`}
          </button>
        ))}
      </div>
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : logs.length === 0 ? <p className="text-[#6B7280] text-sm">No outreach logs yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tracker-table">
            <thead>
              <tr className="border-b border-white/10">
                {['Name','Company','Seq','Step','Sent','Opened','Clicked','Intent','Status'].map(h => (
                  <th key={h} className="text-left py-2 text-[#6B7280] font-mono text-[10px] uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id} className={`border-b border-white/5 ${l.high_intent ? 'bg-[#FF4343]/5' : ''}`}>
                  <td className="py-2 text-white text-xs">{l.prospect_name}</td>
                  <td className="py-2 text-[#9CA3AF] text-xs">{l.prospect_company}</td>
                  <td className="py-2 text-xs font-mono text-[#6B7280]">{l.sequence}</td>
                  <td className="py-2 text-xs font-mono text-[#6B7280]">{l.step}</td>
                  <td className="py-2 text-[#6B7280] text-[10px]">{l.sent_at ? new Date(l.sent_at).toLocaleDateString() : '—'}</td>
                  <td className="py-2">{l.opened ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> : <span className="text-[#6B7280] text-[10px]">—</span>}</td>
                  <td className="py-2">{l.clicked ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> : <span className="text-[#6B7280] text-[10px]">—</span>}</td>
                  <td className="py-2">{l.high_intent ? <span className="text-[10px] font-mono text-[#FF4343] bg-[#FF4343]/10 px-1.5 py-0.5 border border-[#FF4343]/20">HIGH</span> : <span className="text-[#6B7280] text-[10px]">—</span>}</td>
                  <td className="py-2"><span className={`text-[10px] font-mono uppercase ${l.prospect_status === 'high_intent' ? 'text-[#FF4343]' : l.prospect_status === 'suppressed' ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>{l.prospect_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== POST SUBMISSIONS ====================
export function PostSubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => { fetchSubs(); }, []);

  const fetchSubs = async () => {
    try {
      const { data } = await axios.get(`${API}/submissions`, { headers: getAuthHeaders() });
      setSubmissions(data);
    } catch {} finally { setLoading(false); }
  };

  const handleApprove = async (subId) => {
    try {
      await axios.post(`${API}/review/approve`, { submission_id: subId }, { headers: getAuthHeaders() });
      fetchSubs();
      setSelected(null);
    } catch {}
  };

  const handleRequestRevision = async (subId) => {
    try {
      await axios.post(`${API}/review/request-revision`, { submission_id: subId, feedback }, { headers: getAuthHeaders() });
      fetchSubs();
      setSelected(null);
      setFeedback('');
    } catch {}
  };

  const decisionColor = { approve: 'text-[#10B981] bg-[#10B981]/10', flag: 'text-yellow-400 bg-yellow-400/10', reject: 'text-[#FF4343] bg-[#FF4343]/10' };

  if (loading) return <p className="text-[#9CA3AF]">Loading submissions...</p>;

  return (
    <div data-testid="post-submissions-tab">
      {submissions.length === 0 ? <p className="text-[#6B7280] text-sm">No post submissions yet.</p> : (
        <div className="space-y-2">
          {submissions.map(s => (
            <div key={s.id} className={`bg-[#111827] border border-white/10 p-4 cursor-pointer hover:border-white/20 transition-colors ${s.ai_decision === 'flag' ? 'border-l-[3px] border-l-yellow-400' : s.ai_decision === 'reject' ? 'border-l-[3px] border-l-[#FF4343]' : s.ai_decision === 'approve' ? 'border-l-[3px] border-l-[#10B981]' : ''}`}
              onClick={() => setSelected(s)} data-testid={`submission-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-white font-medium">{s.executive_name}</span>
                  <span className="text-[10px] text-[#6B7280]">{s.campaign_title || s.campaign_id}</span>
                  <span className="text-[10px] text-[#6B7280]">{new Date(s.submitted_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  {s.ai_decision && (
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${decisionColor[s.ai_decision] || 'text-[#6B7280]'}`}>
                      {s.ai_decision}
                    </span>
                  )}
                  {s.ai_confidence != null && <span className="text-[10px] font-mono text-[#6B7280]">{s.ai_confidence}%</span>}
                  <ModelBadge model={s.ai_model_used} />
                  <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${s.review_status === 'approved' ? 'text-[#10B981]' : s.review_status === 'revision_requested' ? 'text-yellow-400' : 'text-[#9CA3AF]'}`}>
                    {s.review_status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Detail Panel */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative glass max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="submission-detail">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold">Post Review — {selected.executive_name}</h3>
              <button onClick={() => setSelected(null)} className="text-[#6B7280] hover:text-white text-lg">&times;</button>
            </div>
            {/* AI Summary first */}
            {selected.ai_summary && (
              <div className="bg-[#090E33] border border-white/10 p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-mono uppercase px-2 py-0.5 ${decisionColor[selected.ai_decision] || ''}`}>{selected.ai_decision}</span>
                  <span className="text-[10px] font-mono text-[#6B7280]">Confidence: {selected.ai_confidence}%</span>
                  <ModelBadge model={selected.ai_model_used} />
                </div>
                <p className="text-xs text-[#9CA3AF]">{selected.ai_summary}</p>
              </div>
            )}
            {/* Post Draft */}
            <div className="mb-4">
              <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1 block">Post Draft</label>
              <div className="bg-[#090E33] border border-white/10 p-3 text-xs text-[#9CA3AF] whitespace-pre-wrap leading-relaxed">{selected.post_draft}</div>
            </div>
            {/* Required Points */}
            {selected.required_points_covered && Object.keys(selected.required_points_covered).length > 0 && (
              <div className="mb-4">
                <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1 block">Required Points</label>
                {Object.entries(selected.required_points_covered).map(([point, covered]) => (
                  <div key={point} className="flex items-center gap-2 text-xs py-0.5">
                    {covered ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> : <XCircle className="w-3.5 h-3.5 text-[#FF4343]" />}
                    <span className={covered ? 'text-[#9CA3AF]' : 'text-[#FF4343]'}>{point}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Issues */}
            {selected.ai_issues?.length > 0 && (
              <div className="mb-4">
                <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1 block">Issues</label>
                {selected.ai_issues.map((issue, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs py-1">
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 ${issue.severity === 'blocking' ? 'text-[#FF4343] bg-[#FF4343]/10' : 'text-yellow-400 bg-yellow-400/10'}`}>{issue.severity}</span>
                    <span className="text-[#9CA3AF]">{issue.description}</span>
                  </div>
                ))}
              </div>
            )}
            {/* Suggested Edits */}
            {selected.ai_suggested_edits?.length > 0 && (
              <div className="mb-4">
                <label className="text-[10px] font-mono uppercase text-[#6B7280] mb-1 block">Suggested Edits</label>
                <ul className="space-y-1">
                  {selected.ai_suggested_edits.map((edit, i) => <li key={i} className="text-xs text-[#9CA3AF]">- {edit}</li>)}
                </ul>
              </div>
            )}
            {/* Disclosure */}
            <div className="flex items-center gap-2 text-xs mb-4">
              {selected.disclosure_present ? <CheckCircle className="w-3.5 h-3.5 text-[#10B981]" /> : <XCircle className="w-3.5 h-3.5 text-[#FF4343]" />}
              <span className={selected.disclosure_present ? 'text-[#9CA3AF]' : 'text-[#FF4343]'}>Ad disclosure {selected.disclosure_present ? 'present' : 'missing'}</span>
            </div>
            {/* Actions */}
            {selected.review_status !== 'approved' && (
              <div className="border-t border-white/10 pt-4 space-y-3">
                <button onClick={() => handleApprove(selected.id)} data-testid="approve-submission-btn"
                  className="w-full border-2 border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white py-2 text-sm font-medium transition-colors">
                  Approve & Notify Executive
                </button>
                <div>
                  <textarea value={feedback} onChange={e => setFeedback(e.target.value)} rows={3}
                    placeholder="Revision feedback..." data-testid="revision-feedback"
                    className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-2" />
                  <button onClick={() => handleRequestRevision(selected.id)} data-testid="request-revision-btn"
                    className="w-full border border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-[#090E33] py-2 text-sm font-medium transition-colors">
                    Request Revision
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== TEMPLATE EDITOR ====================

const VARIABLES = ['{{first_name}}', '{{company}}', '{{industry}}', '{{calendar_link}}', '{{deck_url}}'];
const SEQ_LABELS = { A: 'Executive Recruitment', B: 'Brand Outreach' };
const STEP_LABELS = { '1': 'Step 1 — Immediate', '2': 'Step 2 — Day 3', '3': 'Step 3 — Day 7', '4': 'Step 4 — Day 21 (Re-engage)' };
const CHANNEL_ICONS = { email: Mail, linkedin: MessageSquare };

export function TemplateEditorTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ subject: '', body: '', channel: 'email', delay_days: 0 });
  const [previewVars, setPreviewVars] = useState({ first_name: 'Marcus', company: 'Johnson Auto Group', industry: 'Automotive', calendar_link: 'https://calendly.com/freddymedia', deck_url: 'https://freddyleads.com/for-brands' });
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/outreach/templates`, { headers: getAuthHeaders() });
      setTemplates(data);
    } catch {} finally { setLoading(false); }
  };

  const openEditor = (t) => {
    setEditing(t);
    setDraft({ subject: t.subject || '', body: t.body || '', channel: t.channel || 'email', delay_days: t.delay_days || 0 });
    setShowPreview(false);
  };

  const closeEditor = () => {
    setEditing(null);
    setDraft({ subject: '', body: '', channel: 'email', delay_days: 0 });
    setShowPreview(false);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await axios.put(`${API}/outreach/templates/${editing.sequence}/${editing.step}`, {
        ...draft, sequence: editing.sequence, step: editing.step, industry: editing.industry
      }, { headers: getAuthHeaders() });
      fetchTemplates();
      closeEditor();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const insertVariable = (v) => {
    setDraft(d => ({ ...d, body: d.body + v }));
  };

  const renderPreview = (text) => {
    let rendered = text;
    Object.entries(previewVars).forEach(([k, v]) => {
      rendered = rendered.replaceAll(`{{${k}}}`, v);
    });
    return rendered;
  };

  // Group templates by sequence
  const grouped = {};
  templates.forEach(t => {
    const key = t.sequence || 'A';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  });
  // Sort each group by step then channel
  Object.keys(grouped).forEach(k => {
    grouped[k].sort((a, b) => (a.step === b.step ? (a.channel || '').localeCompare(b.channel || '') : (a.step || '').localeCompare(b.step || '')));
  });

  if (loading) return <p className="text-[#9CA3AF]">Loading templates...</p>;

  return (
    <div data-testid="template-editor-tab" className="relative">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-semibold">Outreach Templates</h3>
          <p className="text-[10px] text-[#6B7280] mt-0.5">{templates.length} templates across {Object.keys(grouped).length} sequences</p>
        </div>
      </div>

      {/* Template List */}
      {Object.entries(grouped).map(([seq, items]) => (
        <div key={seq} className="mb-8">
          <h4 className="text-xs font-mono uppercase tracking-wider text-[#FF4343] mb-3 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center border border-[#FF4343]/30 bg-[#FF4343]/5 text-[10px] font-bold">{seq}</span>
            {SEQ_LABELS[seq] || `Sequence ${seq}`}
          </h4>
          <div className="space-y-2">
            {items.map((t, i) => {
              const ChannelIcon = CHANNEL_ICONS[t.channel] || Mail;
              return (
                <div
                  key={`${t.sequence}-${t.step}-${t.channel}-${i}`}
                  className="bg-[#111827] border border-white/10 hover:border-white/20 transition-colors cursor-pointer group"
                  onClick={() => openEditor(t)}
                  data-testid={`template-${t.sequence}-${t.step}-${t.channel}`}
                >
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <ChannelIcon className="w-4 h-4 text-[#6B7280] flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5">{STEP_LABELS[t.step] || `Step ${t.step}`}</span>
                          <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5 capitalize">{t.channel}</span>
                          {t.delay_days > 0 && <span className="text-[10px] text-[#6B7280]">+{t.delay_days}d</span>}
                        </div>
                        <p className="text-xs text-white truncate">{t.subject || '(no subject — LinkedIn message)'}</p>
                        <p className="text-[10px] text-[#6B7280] truncate mt-0.5">{(t.body || '').slice(0, 80)}...</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Pencil className="w-3.5 h-3.5 text-[#FF4343]" />
                      <ChevronRight className="w-4 h-4 text-[#6B7280]" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Editor Drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="template-drawer">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeEditor} />
          <div className="relative w-full max-w-2xl bg-[#111827] border-l border-white/10 overflow-y-auto shadow-2xl">
            {/* Drawer Header */}
            <div className="sticky top-0 z-10 bg-[#111827] border-b border-white/10 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center border border-[#FF4343]/30 bg-[#FF4343]/5 text-[10px] font-bold text-[#FF4343]">{editing.sequence}</span>
                  <h3 className="text-sm font-semibold">{STEP_LABELS[editing.step] || `Step ${editing.step}`}</h3>
                  <span className="text-[10px] font-mono text-[#6B7280] bg-white/5 px-1.5 py-0.5 capitalize">{draft.channel}</span>
                </div>
                <p className="text-[10px] text-[#6B7280] mt-0.5">{SEQ_LABELS[editing.sequence]}</p>
              </div>
              <button onClick={closeEditor} data-testid="close-drawer-btn" className="text-[#6B7280] hover:text-white transition-colors p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Variable Tokens */}
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-[#6B7280] mb-2 block">Insert Variable</label>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES.map(v => (
                    <button key={v} onClick={() => insertVariable(v)} data-testid={`var-${v}`}
                      className="text-[10px] font-mono px-2 py-1 border border-[#FF4343]/20 text-[#FF4343] bg-[#FF4343]/5 hover:bg-[#FF4343]/10 transition-colors">
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              {draft.channel === 'email' && (
                <div>
                  <label className="text-[10px] font-mono uppercase tracking-wider text-[#6B7280] mb-1.5 block">Subject Line</label>
                  <input
                    value={draft.subject}
                    onChange={e => setDraft({ ...draft, subject: e.target.value })}
                    data-testid="template-subject-input"
                    className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] transition-colors"
                    placeholder="Email subject line..."
                  />
                </div>
              )}

              {/* Body */}
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-[#6B7280] mb-1.5 block">
                  {draft.channel === 'email' ? 'Email Body' : 'LinkedIn Message'}
                </label>
                <textarea
                  value={draft.body}
                  onChange={e => setDraft({ ...draft, body: e.target.value })}
                  rows={12}
                  data-testid="template-body-input"
                  className="w-full px-3 py-2.5 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none font-mono leading-relaxed transition-colors"
                  placeholder="Message body..."
                />
                <p className="text-[9px] text-[#6B7280] mt-1">{draft.body.length} characters</p>
              </div>

              {/* Delay */}
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-[#6B7280] mb-1.5 block">Send Delay (days after sequence start)</label>
                <input
                  type="number" min="0" value={draft.delay_days}
                  onChange={e => setDraft({ ...draft, delay_days: parseInt(e.target.value) || 0 })}
                  data-testid="template-delay-input"
                  className="w-24 px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm focus:outline-none focus:border-[#FF4343] transition-colors"
                />
              </div>

              {/* Preview Toggle */}
              <div className="border-t border-white/10 pt-5">
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  data-testid="toggle-preview-btn"
                  className="flex items-center gap-2 text-xs text-[#9CA3AF] hover:text-white transition-colors mb-3"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {showPreview ? 'Hide Preview' : 'Show Rendered Preview'}
                </button>

                {showPreview && (
                  <div className="space-y-3">
                    {/* Preview Variable Overrides */}
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(previewVars).map(([k, v]) => (
                        <div key={k}>
                          <label className="text-[9px] font-mono text-[#6B7280]">{k}</label>
                          <input value={v} onChange={e => setPreviewVars({ ...previewVars, [k]: e.target.value })}
                            className="w-full px-2 py-1 bg-[#090E33] border border-white/10 text-white text-[10px] focus:outline-none focus:border-[#FF4343]" />
                        </div>
                      ))}
                    </div>
                    {/* Rendered Preview */}
                    <div className="bg-[#090E33] border border-[#FF4343]/20 p-4" data-testid="rendered-preview">
                      {draft.channel === 'email' && (
                        <div className="mb-3 pb-3 border-b border-white/10">
                          <span className="text-[9px] font-mono text-[#6B7280]">Subject:</span>
                          <p className="text-sm text-white mt-0.5">{renderPreview(draft.subject)}</p>
                        </div>
                      )}
                      <span className="text-[9px] font-mono text-[#6B7280]">Body:</span>
                      <pre className="text-xs text-[#9CA3AF] mt-1 whitespace-pre-wrap font-sans leading-relaxed">{renderPreview(draft.body)}</pre>
                    </div>
                  </div>
                )}
              </div>

              {/* Save / Cancel */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-template-btn"
                  className="flex items-center gap-2 border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-5 py-2.5 text-sm font-semibold transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button
                  onClick={closeEditor}
                  className="border border-white/20 text-[#9CA3AF] hover:text-white px-5 py-2.5 text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}